/**
 * DocumentLogger - v2 Taxonomy Wrapper for document save/load logging
 *
 * Domain: document
 * Taxonomy: document.{operation}[.{flavor}]
 *
 * Operations:
 * - load: Document loading operations
 * - save: Document saving operations (manual, auto, direct, working-copy)
 * - create: Document creation
 * - version: Version management
 * - autosave: Auto-save operations
 * - workingCopy: Working copy management
 * - ast: AST conversion operations
 * - connectivity: Online/offline state changes
 *
 * Flavors: .start, .complete, .fail, .perf, .trigger
 *
 * All methods capture full stack traces via captureCallerSource().
 *
 * @example
 * DocumentLogger.load.start(documentId, { versionId });
 * DocumentLogger.save.complete(documentId, { saveType: 'manual', saveTimeMs: 150 });
 */

import { WeBelieveLogger, captureCallerSource } from '../WeBelieveLogger';

export const DocumentLogger = {
  // ============================================================================
  // LOAD OPERATIONS - document.load.*
  // ============================================================================
  load: {
    /**
     * Log document load started
     * Component: document.load.start
     */
    start: (documentId: string, options?: { versionId?: string; loadWorkingCopy?: boolean }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.load.start', 'Document load started', source, {
        documentId,
        ...options,
      });
    },

    /**
     * Log document load completed with performance
     * Component: document.load.complete
     */
    complete: (documentId: string, data: {
      title?: string;
      contentLength: number;
      versionsCount?: number;
      hasWorkingCopy?: boolean;
      loadTimeMs?: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('document.load.complete', 'Document load complete', data.loadTimeMs || 0, source, {
        documentId,
        title: data.title,
        contentLength: data.contentLength,
        versionsCount: data.versionsCount,
        hasWorkingCopy: data.hasWorkingCopy,
      });
    },

    /**
     * Log document load failed
     * Component: document.load.fail
     */
    fail: (documentId: string, error: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'document.load.fail', `Document load failed: ${error}`, source, {
        documentId,
        error,
      });
    },
  },

  // ============================================================================
  // SAVE OPERATIONS - document.save.*
  // ============================================================================
  save: {
    /**
     * Log document save started
     * Component: document.save.start
     */
    start: (documentId: string, saveType: 'manual' | 'auto' | 'direct' | 'working-copy') => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.save.start', `Document save started (${saveType})`, source, {
        documentId,
        saveType,
      });
    },

    /**
     * Log document save completed with performance
     * Component: document.save.complete
     */
    complete: (documentId: string, data: {
      saveType: 'manual' | 'auto' | 'direct' | 'working-copy';
      contentLength?: number;
      saveTimeMs?: number;
      versionId?: string;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('document.save.complete', `Document save complete (${data.saveType})`, data.saveTimeMs || 0, source, {
        documentId,
        saveType: data.saveType,
        contentLength: data.contentLength,
        versionId: data.versionId,
      });
    },

    /**
     * Log document save failed
     * Component: document.save.fail
     */
    fail: (documentId: string, saveType: string, error: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'document.save.fail', `Document save failed (${saveType}): ${error}`, source, {
        documentId,
        saveType,
        error,
      });
    },
  },

  // ============================================================================
  // CREATE OPERATIONS - document.create.*
  // ============================================================================
  create: {
    /**
     * Log document creation started
     * Component: document.create.start
     */
    start: (title: string, documentType?: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.create.start', 'Document creation started', source, {
        title,
        documentType,
      });
    },

    /**
     * Log document creation completed with performance
     * Component: document.create.complete
     */
    complete: (documentId: string, data: {
      title: string;
      versionId?: string;
      createTimeMs?: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('document.create.complete', 'Document creation complete', data.createTimeMs || 0, source, {
        documentId,
        title: data.title,
        versionId: data.versionId,
      });
    },

    /**
     * Log document creation failed
     * Component: document.create.fail
     */
    fail: (title: string, error: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'document.create.fail', `Document creation failed: ${error}`, source, {
        title,
        error,
      });
    },
  },

  // ============================================================================
  // VERSION OPERATIONS - document.version.*
  // ============================================================================
  version: {
    /**
     * Log new version created
     * Component: document.version.create
     */
    create: (documentId: string, versionNumber: number, versionId: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.version.create', 'New version created', source, {
        documentId,
        versionNumber,
        versionId,
      });
    },

    /**
     * Log version loaded
     * Component: document.version.load
     */
    load: (documentId: string, versionId: string, versionNumber: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.version.load', 'Version loaded', source, {
        documentId,
        versionId,
        versionNumber,
      });
    },
  },

  // ============================================================================
  // AUTO-SAVE OPERATIONS - document.autosave.*
  // ============================================================================
  autosave: {
    /**
     * Log auto-save triggered
     * Component: document.autosave.trigger
     */
    trigger: (documentId: string, trigger: 'interval' | 'idle' | 'changes') => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.autosave.trigger', `Auto-save triggered (${trigger})`, source, {
        documentId,
        trigger,
      });
    },

    /**
     * Log auto-save completed with performance
     * Component: document.autosave.complete
     */
    complete: (documentId: string, saveTimeMs: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('document.autosave.complete', 'Auto-save complete', saveTimeMs, source, {
        documentId,
      });
    },

    /**
     * Log auto-save failed
     * Component: document.autosave.fail
     */
    fail: (documentId: string, error: string, attempt: number, maxRetries: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('warn', 'document.autosave.fail', `Auto-save failed (attempt ${attempt}/${maxRetries})`, source, {
        documentId,
        error,
        attempt,
        maxRetries,
      });
    },
  },

  // ============================================================================
  // WORKING COPY OPERATIONS - document.workingCopy.*
  // ============================================================================
  workingCopy: {
    /**
     * Log working copy saved
     * Component: document.workingCopy.save
     */
    save: (documentId: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.workingCopy.save', 'Working copy saved', source, {
        documentId,
      });
    },

    /**
     * Log working copy cleared
     * Component: document.workingCopy.clear
     */
    clear: (documentId: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.workingCopy.clear', 'Working copy cleared', source, {
        documentId,
      });
    },

    /**
     * Log working copy committed
     * Component: document.workingCopy.commit
     */
    commit: (documentId: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.workingCopy.commit', 'Working copy committed to database', source, {
        documentId,
      });
    },
  },

  // ============================================================================
  // AST CONVERSION OPERATIONS - document.ast.*
  // ============================================================================
  ast: {
    /**
     * Log AST conversion started
     * Component: document.ast.start
     */
    start: (direction: 'text-to-ast' | 'ast-to-text', contentLength: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.ast.start', `AST conversion started (${direction})`, source, {
        direction,
        contentLength,
      });
    },

    /**
     * Log AST conversion completed with performance
     * Component: document.ast.complete
     */
    complete: (direction: 'text-to-ast' | 'ast-to-text', data: {
      inputLength: number;
      outputLength?: number;
      conversionTimeMs: number;
      warnings?: string[];
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.performanceWithSource('document.ast.complete', `AST conversion complete (${direction})`, data.conversionTimeMs, source, {
        direction,
        inputLength: data.inputLength,
        outputLength: data.outputLength,
        warningsCount: data.warnings?.length || 0,
      });
    },

    /**
     * Log AST conversion failed
     * Component: document.ast.fail
     */
    fail: (direction: string, error: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('error', 'document.ast.fail', `AST conversion failed (${direction}): ${error}`, source, {
        direction,
        error,
      });
    },

    /**
     * Log state capture for AST conversion
     * Component: document.ast.capture
     */
    capture: (data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.ast.capture', 'Capturing state for AST', source, data);
    },

    /**
     * Log AST conversion step
     * Component: document.ast.convert
     */
    convert: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.ast.convert', message, source, data);
    },

    /**
     * Log AST created successfully
     * Component: document.ast.created
     */
    created: (data: {
      paragraphCount: number;
      totalLength?: number;
      documentId?: string;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.ast.created', `AST created with ${data.paragraphCount} paragraphs`, source, data);
    },

    /**
     * Log grapheme slice operation
     * Component: document.ast.graphemeSlice
     */
    graphemeSlice: (message: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.ast.graphemeSlice', message, source, data);
    },
  },

  // ============================================================================
  // CONNECTIVITY OPERATIONS - document.connectivity.*
  // ============================================================================
  connectivity: {
    /**
     * Log connectivity changed
     * Component: document.connectivity.change
     */
    change: (isOnline: boolean, pendingWorkingCopies: number) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.connectivity.change', `Connectivity changed: ${isOnline ? 'online' : 'offline'}`, source, {
        isOnline,
        pendingWorkingCopies,
      });
    },

    /**
     * Log save queued for offline sync
     * Component: document.connectivity.queued
     */
    queued: (documentId: string) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.connectivity.queued', 'Save queued for offline sync', source, {
        documentId,
      });
    },
  },

  // ============================================================================
  // DEFAULTS OPERATIONS - document.defaults.*
  // Tracks resolution of default values from System Preferences for new documents
  // ============================================================================
  defaults: {
    /**
     * Log line height/spacing default resolved
     * Component: document.defaults.lineHeight
     */
    lineHeight: (data: {
      resolvedValue: number;
      source: 'userDefaults' | 'systemPreferences' | 'hardcoded';
      systemPrefsValue?: string;
      userDefaultsValue?: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.defaults.lineHeight', `Line height resolved: ${data.resolvedValue} (from ${data.source})`, source, {
        resolvedValue: data.resolvedValue,
        source: data.source,
        systemPrefsValue: data.systemPrefsValue,
        userDefaultsValue: data.userDefaultsValue,
      });
    },

    /**
     * Log font family default resolved
     * Component: document.defaults.fontFamily
     */
    fontFamily: (data: {
      resolvedValue: string;
      source: 'userDefaults' | 'systemPreferences' | 'css' | 'hardcoded';
      systemPrefsValue?: string;
      userDefaultsValue?: string;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.defaults.fontFamily', `Font family resolved: ${data.resolvedValue} (from ${data.source})`, source, {
        resolvedValue: data.resolvedValue,
        source: data.source,
        systemPrefsValue: data.systemPrefsValue,
        userDefaultsValue: data.userDefaultsValue,
      });
    },

    /**
     * Log font size default resolved
     * Component: document.defaults.fontSize
     */
    fontSize: (data: {
      resolvedValue: number;
      source: 'userDefaults' | 'systemPreferences' | 'css' | 'hardcoded';
      systemPrefsScale?: string;
      userDefaultsValue?: number;
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.defaults.fontSize', `Font size resolved: ${data.resolvedValue}px (from ${data.source})`, source, {
        resolvedValue: data.resolvedValue,
        source: data.source,
        systemPrefsScale: data.systemPrefsScale,
        userDefaultsValue: data.userDefaultsValue,
      });
    },

    /**
     * Log page setup defaults resolved
     * Component: document.defaults.pageSetup
     */
    pageSetup: (data: {
      pageSize: string;
      orientation: string;
      margins: { top: number; right: number; bottom: number; left: number };
      source: 'userDefaults' | 'systemPreferences' | 'hardcoded';
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.defaults.pageSetup', `Page setup resolved (from ${data.source})`, source, {
        pageSize: data.pageSize,
        orientation: data.orientation,
        margins: data.margins,
        source: data.source,
      });
    },

    /**
     * Log all font settings resolved together
     * Component: document.defaults.fontSettings
     */
    fontSettings: (data: {
      fontFamily: string;
      fontSizePx: number;
      lineHeightMul: number;
      sources: {
        fontFamily: 'userDefaults' | 'systemPreferences' | 'css' | 'hardcoded';
        fontSize: 'userDefaults' | 'systemPreferences' | 'css' | 'hardcoded';
        lineHeight: 'userDefaults' | 'systemPreferences' | 'hardcoded';
      };
    }) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('info', 'document.defaults.fontSettings', 'Font settings resolved for new document', source, {
        fontFamily: data.fontFamily,
        fontSizePx: data.fontSizePx,
        lineHeightMul: data.lineHeightMul,
        sources: data.sources,
      });
    },
  },

  // ============================================================================
  // API OPERATIONS - document.api.*
  // HTTP request logging for document save/load operations
  // ============================================================================
  api: {
    /**
     * Log API POST request for document save
     * Component: document.api.post
     */
    post: (url: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.api.post', `POST ${url}`, source, data);
    },

    /**
     * Log API response success
     * Component: document.api.success
     */
    success: (operation: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.api.success', `${operation} successful`, source, data);
    },

    /**
     * Log API request payload structure
     * Component: document.api.payload
     */
    payload: (description: string, data?: Record<string, unknown>) => {
      const source = captureCallerSource();
      WeBelieveLogger.logWithSource('debug', 'document.api.payload', description, source, data);
    },
  },

  // ============================================================================
  // LEGACY COMPATIBILITY - Maps old method signatures to v2 taxonomy
  // These maintain backward compatibility while emitting v2 component names
  // ============================================================================

  /** @deprecated Use DocumentLogger.load.start() */
  loadStarted: (documentId: string, options?: { versionId?: string; loadWorkingCopy?: boolean }) => {
    DocumentLogger.load.start(documentId, options);
  },

  /** @deprecated Use DocumentLogger.load.complete() */
  loadCompleted: (documentId: string, data: {
    title?: string;
    contentLength: number;
    versionsCount?: number;
    hasWorkingCopy?: boolean;
    loadTimeMs?: number;
  }) => {
    DocumentLogger.load.complete(documentId, data);
  },

  /** @deprecated Use DocumentLogger.load.fail() */
  loadFailed: (documentId: string, error: string) => {
    DocumentLogger.load.fail(documentId, error);
  },

  /** @deprecated Use DocumentLogger.save.start() */
  saveStarted: (documentId: string, saveType: 'manual' | 'auto' | 'direct' | 'working-copy') => {
    DocumentLogger.save.start(documentId, saveType);
  },

  /** @deprecated Use DocumentLogger.save.complete() */
  saveCompleted: (documentId: string, data: {
    saveType: 'manual' | 'auto' | 'direct' | 'working-copy';
    contentLength?: number;
    saveTimeMs?: number;
    versionId?: string;
  }) => {
    DocumentLogger.save.complete(documentId, data);
  },

  /** @deprecated Use DocumentLogger.save.fail() */
  saveFailed: (documentId: string, saveType: string, error: string) => {
    DocumentLogger.save.fail(documentId, saveType, error);
  },

  /** @deprecated Use DocumentLogger.create.start() */
  createStarted: (title: string, documentType?: string) => {
    DocumentLogger.create.start(title, documentType);
  },

  /** @deprecated Use DocumentLogger.create.complete() */
  createCompleted: (documentId: string, data: {
    title: string;
    versionId?: string;
    createTimeMs?: number;
  }) => {
    DocumentLogger.create.complete(documentId, data);
  },

  /** @deprecated Use DocumentLogger.create.fail() */
  createFailed: (title: string, error: string) => {
    DocumentLogger.create.fail(title, error);
  },

  /** @deprecated Use DocumentLogger.version.create() */
  versionCreated: (documentId: string, versionNumber: number, versionId: string) => {
    DocumentLogger.version.create(documentId, versionNumber, versionId);
  },

  /** @deprecated Use DocumentLogger.version.load() */
  versionLoaded: (documentId: string, versionId: string, versionNumber: number) => {
    DocumentLogger.version.load(documentId, versionId, versionNumber);
  },

  /** @deprecated Use DocumentLogger.autosave.trigger() */
  autoSaveTriggered: (documentId: string, trigger: 'interval' | 'idle' | 'changes') => {
    DocumentLogger.autosave.trigger(documentId, trigger);
  },

  /** @deprecated Use DocumentLogger.autosave.complete() */
  autoSaveCompleted: (documentId: string, saveTimeMs: number) => {
    DocumentLogger.autosave.complete(documentId, saveTimeMs);
  },

  /** @deprecated Use DocumentLogger.autosave.fail() */
  autoSaveFailed: (documentId: string, error: string, attempt: number, maxRetries: number) => {
    DocumentLogger.autosave.fail(documentId, error, attempt, maxRetries);
  },

  /** @deprecated Use DocumentLogger.workingCopy.save() */
  workingCopySaved: (documentId: string) => {
    DocumentLogger.workingCopy.save(documentId);
  },

  /** @deprecated Use DocumentLogger.workingCopy.clear() */
  workingCopyCleared: (documentId: string) => {
    DocumentLogger.workingCopy.clear(documentId);
  },

  /** @deprecated Use DocumentLogger.workingCopy.commit() */
  workingCopyCommitted: (documentId: string) => {
    DocumentLogger.workingCopy.commit(documentId);
  },

  /** @deprecated Use DocumentLogger.ast.start() */
  astConversionStarted: (direction: 'text-to-ast' | 'ast-to-text', contentLength: number) => {
    DocumentLogger.ast.start(direction, contentLength);
  },

  /** @deprecated Use DocumentLogger.ast.complete() */
  astConversionCompleted: (direction: 'text-to-ast' | 'ast-to-text', data: {
    inputLength: number;
    outputLength?: number;
    conversionTimeMs: number;
    warnings?: string[];
  }) => {
    DocumentLogger.ast.complete(direction, data);
  },

  /** @deprecated Use DocumentLogger.ast.fail() */
  astConversionFailed: (direction: string, error: string) => {
    DocumentLogger.ast.fail(direction, error);
  },

  /** @deprecated Use DocumentLogger.connectivity.change() */
  connectivityChanged: (isOnline: boolean, pendingWorkingCopies: number) => {
    DocumentLogger.connectivity.change(isOnline, pendingWorkingCopies);
  },

  /** @deprecated Use DocumentLogger.connectivity.queued() */
  offlineSaveQueued: (documentId: string) => {
    DocumentLogger.connectivity.queued(documentId);
  },
};

export default DocumentLogger;
